import { FormControl, FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    CouponDetailInterface,
    CouponsService,
    HttpStatusCodesEnum
} from '@spa/api';

import { RequiredValidatorKey } from '@spa/validators';
import {
    CouponStatusControlOptions,
    CouponStatusTriggerFor,
    CouponStatusesControl
} from 'appAssets/descriptors/panel-coupons.descriptor';
import { Helper, RadioGroupControl } from 'appShared/classes';
import { RadioGroupOptionInterface } from 'appShared/components';

export class CouponStatusForm {
    private couponsService: CouponsService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(couponsService: CouponsService) {
        this.couponsService = couponsService;
    }

    /**
     * A status is offered only when the API currently reports a trigger reaching it. The coupon's
     * own status stays selectable so the group always has a checked option.
     */
    private applyAvailability(
        coupon: CouponDetailInterface,
        availableTriggers: number[],
        disabledControlItems: boolean
    ): void {
        this.control.options.forEach((option: RadioGroupOptionInterface) => {
            const reachable = CouponStatusTriggerFor(option.value as number, availableTriggers) != null;
            const isCurrent = option.value === coupon?.status;

            option.disabled = disabledControlItems || (!reachable && !isCurrent);
            option.checked = isCurrent;

            if (isCurrent) {
                this.control.value = coupon.status;
            }
        });
    }

    initForm(coupon: CouponDetailInterface, availableTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = CouponStatusesControl();
        this.control.options = CouponStatusControlOptions();

        this.applyAvailability(coupon, availableTriggers, disabledControlItems);

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    refreshForm(coupon: CouponDetailInterface, availableTriggers: number[], disabledControlItems: boolean): void {
        this.applyAvailability(coupon, availableTriggers, disabledControlItems);
        this.formGroup.get(this.control.name).setValue(this.control.value);
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    /** Resolves to a no-op success when the selection has not moved off the current status. */
    createRequest(coupon: CouponDetailInterface, availableTriggers: number[]): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name];

        if (coupon.status === status) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        const trigger = CouponStatusTriggerFor(status, availableTriggers);

        if (trigger == null) {
            return Helper.createSuccessResponseObservable<boolean>(true);
        }

        return this.couponsService.changeStatus(coupon.id, trigger)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(response);
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }
}
